import { useId } from 'react'

import {
  ATTRIBUTION_GROUP_BY_LABELS,
  type AttributionMetrics,
  type AttributionReport,
  type AttributionRow,
} from '@/api/attribution'
import { deltaPresentation, type DeltaPresentation } from '@/components/dataQuality'
import { tmtAttributedLabel } from '@/components/TmtAttributedBadge'
import { DataTable, type Column, type Polarity } from '@/design'

import { ShareBadge, SpendBadge, TmtBadge } from './AttributionBadges'
import { Figure, NameCell, Sourced, type SourceRef } from './cells'
import {
  cpaMinor,
  hasSpend,
  NOT_TRACKED_SHARE_DETAIL,
  roas,
  rowKey,
  rowKind,
  rowLabel,
  rowSpendMinor,
  rowsWithUnattributed,
  shareHeading,
  shareOf,
  spendSourceLabel,
  splitsNotTracked,
  UNATTRIBUTED_LABELS,
} from './definitions'
import { count, money, ratio, share, unattributedShareText } from './format'

/** Every figure's source on this table, bound once: TmT's under the report's model, and the spend columns'. */
interface Sources {
  tmt: SourceRef
  spend: SourceRef
  /** How every change is shown: neutral when the comparison straddles the tracking baseline. */
  comparison: DeltaPresentation
}

/**
 * The four TmT metrics, described once: the totals and every row render from these, so a
 * figure's format, change and polarity cannot differ between the two.
 */
interface TmtMetric {
  key: string
  label: string
  format: (metrics: AttributionMetrics, currency: string) => string
  /** The number its previous-period change is worked out on. */
  measure: (metrics: AttributionMetrics) => number
  polarity: Polarity
}

const TMT_METRICS: readonly TmtMetric[] = [
  { key: 'purchases', label: 'Purchases', format: (m) => count(m.purchases), measure: (m) => m.purchases, polarity: 'up_good' },
  {
    key: 'revenue',
    label: 'Revenue',
    format: (m, currency) => money(m.revenueMinor, currency),
    measure: (m) => m.revenueMinor,
    polarity: 'up_good',
  },
  { key: 'newCustomers', label: 'New customers', format: (m) => count(m.newCustomers), measure: (m) => m.newCustomers, polarity: 'up_good' },
  {
    key: 'refunds',
    label: 'Refunds',
    format: (m, currency) => `${count(m.refunds)} · ${money(m.refundMinor, currency)}`,
    measure: (m) => m.refundMinor,
    polarity: 'down_good',
  },
]

/** One TmT metric for one set of figures, sourced, with its change when there is a previous period. */
function MetricFigure({
  metric,
  metrics,
  previous,
  currency,
  source,
  comparison,
}: {
  metric: TmtMetric
  metrics: AttributionMetrics
  previous: AttributionMetrics | null
  currency: string
  source: SourceRef
  comparison: DeltaPresentation
}) {
  return (
    <Sourced by={source}>
      <Figure
        value={metric.format(metrics, currency)}
        current={metric.measure(metrics)}
        previous={previous ? metric.measure(previous) : null}
        polarity={metric.polarity}
        comparison={comparison}
      />
    </Sourced>
  )
}

/** Which badge covers which figures, each with its full caveat in a tooltip; every figure below is described by its entry. */
function SourceLegend({ report, sources }: { report: AttributionReport; sources: Sources }) {
  return (
    <ul className="attr-legend" aria-label="Where these figures come from">
      <li id={sources.tmt.describedBy}>
        <TmtBadge model={report.model} lookbackDays={report.lookbackDays} />
        <span>Purchases, revenue, new customers, refunds and shares</span>
      </li>
      <li id={sources.spend.describedBy}>
        <SpendBadge report={report} />
        <span>Spend, CPA and ROAS</span>
      </li>
    </ul>
  )
}

/**
 * The report's totals, with the unattributed share among them — named by what the row
 * holds at this grouping. From an older API it is always stated, a zero share included;
 * once the report splits it out it is the Not tracked share, stated when the API sent
 * the Not tracked row.
 */
function AttributionTotals({ report, sources }: { report: AttributionReport; sources: Sources }) {
  const shareText = unattributedShareText(report)
  const split = splitsNotTracked(report)
  return (
    <dl className="attr-totals" aria-label="Totals">
      {TMT_METRICS.map((metric) => (
        <div key={metric.key}>
          <dt>{metric.label}</dt>
          <dd>
            <MetricFigure
              metric={metric}
              metrics={report.totals}
              previous={report.totalsPrevious}
              currency={report.currency}
              source={sources.tmt}
              comparison={sources.comparison}
            />
          </dd>
        </div>
      ))}
      {shareText ? (
        <div className="attr-totals__unattributed" data-testid="unattributed-share">
          <dt>
            {shareHeading(split)} <ShareBadge report={report} />
          </dt>
          <dd className="attr-totals__detail">{split ? NOT_TRACKED_SHARE_DETAIL : UNATTRIBUTED_LABELS[report.groupBy]}</dd>
          <dd>
            <Sourced by={sources.tmt}>{shareText.revenue}</Sourced>
          </dd>
          <dd className="attr-totals__detail">{shareText.detail}</dd>
        </div>
      ) : null}
      {report.spendSource === 'meta' ? (
        <div>
          <dt>Spend</dt>
          <dd>
            <Sourced by={sources.spend}>{money(hasSpend(report) ? report.totalSpendMinor : null, report.currency)}</Sourced>
          </dd>
        </div>
      ) : null}
    </dl>
  )
}

function columnsFor(report: AttributionReport, sources: Sources): Column<AttributionRow>[] {
  const { currency, totals, groupBy } = report
  const split = splitsNotTracked(report)
  const spendColumn = (key: string, header: string, value: (row: AttributionRow) => string): Column<AttributionRow> => ({
    key,
    header,
    align: 'end',
    cell: (row) => <Sourced by={sources.spend}>{value(row)}</Sourced>,
  })
  return [
    {
      key: 'name',
      header: ATTRIBUTION_GROUP_BY_LABELS[groupBy],
      cell: (row) => <NameCell label={rowLabel(row, groupBy, split)} kind={rowKind(row, split)} report={report} />,
    },
    ...TMT_METRICS.map(
      (metric): Column<AttributionRow> => ({
        key: metric.key,
        header: metric.label,
        align: 'end',
        cell: (row) => (
          <MetricFigure
            metric={metric}
            metrics={row.metrics}
            previous={row.previous}
            currency={currency}
            source={sources.tmt}
            comparison={sources.comparison}
          />
        ),
      }),
    ),
    {
      key: 'share',
      header: 'Share of revenue',
      align: 'end',
      cell: (row) => <Sourced by={sources.tmt}>{share(shareOf(row.metrics.revenueMinor, totals.revenueMinor))}</Sourced>,
    },
    spendColumn('spend', 'Spend', (row) => money(rowSpendMinor(report, row), currency)),
    spendColumn('cpa', 'CPA', (row) => money(cpaMinor(rowSpendMinor(report, row), row.metrics.purchases), currency)),
    spendColumn('roas', 'ROAS', (row) => ratio(roas(row.metrics.revenueMinor, rowSpendMinor(report, row)))),
  ]
}

/**
 * The source legend, the totals and one row per group, the unattributed (or, since the
 * coverage card, the Not tracked) row pinned last and never filtered out. Rows arrive
 * largest revenue first; the table does not re-sort, so it cannot be sorted away from
 * the bottom.
 */
export function AttributionTable({ report }: { report: AttributionReport }) {
  const base = useId()
  const sources: Sources = {
    tmt: { source: tmtAttributedLabel(report.model), describedBy: `${base}-tmt` },
    spend: { source: spendSourceLabel(report), describedBy: `${base}-spend` },
    comparison: deltaPresentation(report.dataQuality),
  }
  return (
    <>
      <SourceLegend report={report} sources={sources} />
      <AttributionTotals report={report} sources={sources} />
      <DataTable
        caption={`TmT-attributed sales by ${ATTRIBUTION_GROUP_BY_LABELS[report.groupBy].toLowerCase()}`}
        rows={rowsWithUnattributed(report)}
        rowKey={rowKey}
        columns={columnsFor(report, sources)}
      />
    </>
  )
}
